/*
 * Printouts.
 *
 * The backup chart is the important one. If the system, the tablet or the Wi-Fi is down, the
 * round still happens, so a blank chart for the week ahead lives in the folder and gets used the
 * moment anything stops working. Everything on it is set out the way the screen sets it out, so
 * nobody has to think twice at 8am.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';

type BackupRow = {
  medicine: string;
  doseText: string;
  time: string;
  when: string;
  note: string;
  controlledDrug: boolean;
  timeCritical: boolean;
  trayType: 'tray' | 'separate' | 'none';
};

type BackupResident = {
  id: string;
  name: string;
  room: string;
  dateOfBirth: string;
  allergies: string[];
  rows: BackupRow[];
  whenRequired: Array<{
    medicine: string; doseText: string; reason: string; directions: string;
    minGap: number | null; max24h: number | null; controlledDrug: boolean;
  }>;
};

type Backup = {
  home: string;
  from: string;
  dates: string[];
  printedAt: string;
  printedBy: string;
  residents: BackupResident[];
};

type Summary = {
  printedAt: string;
  printedBy: string;
  home: { name: string; address: string[]; phone: string };
  resident: { name: string; room: string; dateOfBirth: string; allergies: string[] };
  medicines: Array<{ medicine: string; directions: string; schedule: string; route: string; controlledDrug: boolean }>;
  whenRequired: Array<{ medicine: string; directions: string; route: string; controlledDrug: boolean }>;
  recentlyStopped: Array<{ medicine: string; stoppedOn: string; reason: string }>;
  fluids: { targetMl: number | null; thickenerLevel: number | null; thickenerNote: string } | null;
  inr: { value: number; testedOn: string; doseText: string; nextTestOn: string | null } | null;
};

/*
 * With a resident, it opens straight onto that resident's backup chart — the resident's page uses
 * it that way — and Back goes back to them rather than to the menu.
 */
export function PrintoutsScreen({ homeId, residentId, onBack }: { homeId: string; residentId?: string; onBack?: () => void }) {
  const [what, setWhat] = useState<'menu' | 'backup' | 'summary'>('menu');
  const [backup, setBackup] = useState<Backup | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [residents, setResidents] = useState<Array<{ id: string; firstName: string; lastName: string; room: string }>>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<{ residents: Array<{ id: string; firstName: string; lastName: string; room: string }> }>(
        `/api/homes/${homeId}/residents`,
      )
      .then((data) => setResidents(data.residents))
      .catch(() => setResidents([]));
  }, [homeId]);

  const loadBackup = useCallback(async () => {
    try {
      const one = residentId ? `&residentId=${residentId}` : '';
      setBackup(await api.get<Backup>(`/api/homes/${homeId}/backup-chart?days=7${one}`));
      setWhat('backup');
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not build the chart.');
    }
  }, [homeId, residentId]);

  useEffect(() => {
    if (residentId) void loadBackup();
  }, [residentId, loadBackup]);

  const loadSummary = useCallback(async (residentId: string) => {
    try {
      setSummary(await api.get<Summary>(`/api/residents/${residentId}/summary`));
      setWhat('summary');
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not build the summary.');
    }
  }, []);

  if (what === 'backup' && backup) {
    return (
      <section className="printout">
        <div className="head no-print">
          <div>
            <h1>Backup chart</h1>
            <p className="muted small">
              Seven days from {formatDate(backup.from)}. Print it and keep it in the folder.
            </p>
          </div>
          <div className="row-actions">
            <button type="button" onClick={() => window.print()}>Print</button>
            <button type="button" className="quiet" onClick={() => (onBack ? onBack() : setWhat('menu'))}>Back</button>
          </div>
        </div>

        {backup.residents.map((resident) => (
          <article className="sheet" key={resident.id}>
            <header className="sheet-head">
              <div>
                <h2>{resident.name}</h2>
                <p>
                  {resident.room ? `Room ${resident.room} · ` : ''}born {formatDate(resident.dateOfBirth)} ·{' '}
                  {backup.home}
                </p>
              </div>
              <div className="sheet-note">
                <strong>Use this only if the system is down.</strong>
                <span>Sign each box as you give it, then enter it on the system when it is back.</span>
              </div>
            </header>

            <p className={resident.allergies.length ? 'allergy' : 'muted small'}>
              {resident.allergies.length ? `ALLERGIES: ${resident.allergies.join(', ')}` : 'No allergies recorded'}
            </p>

            <table className="mar">
              <thead>
                <tr>
                  <th className="med">Medicine</th>
                  <th className="at">Time</th>
                  {backup.dates.map((date) => (
                    <th className="day" key={date}>
                      <span>{shortDay(date)}</span>
                      <span className="muted small">{shortDate(date)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resident.rows.map((row, index) => (
                  <tr key={`${row.medicine}-${row.time}-${index}`}>
                    <th className="med" scope="row">
                      {row.medicine}
                      <small>
                        {row.doseText}
                        {qualifier(row.when) ? ` · ${qualifier(row.when)}` : ''}
                        {row.trayType !== 'none' ? ' · in the tray' : ''}
                        {row.controlledDrug ? ' · controlled drug, two signatures' : ''}
                        {row.timeCritical ? ' · time critical' : ''}
                        {row.note ? ` · ${row.note}` : ''}
                      </small>
                    </th>
                    <td className="at">{row.time}</td>
                    {backup.dates.map((date) => (
                      <td className="box" key={date} />
                    ))}
                  </tr>
                ))}
                {!resident.rows.length ? (
                  <tr><td colSpan={backup.dates.length + 2}>Nothing due at a set time.</td></tr>
                ) : null}
              </tbody>
            </table>

            {resident.whenRequired.length ? (
              <>
                <h3>When required</h3>
                <table className="register">
                  <thead>
                    <tr><th>Medicine</th><th>For</th><th>Limits</th><th>Date and time</th><th>Given</th><th>Signed</th></tr>
                  </thead>
                  <tbody>
                    {resident.whenRequired.flatMap((med) =>
                      [0, 1, 2].map((line) => (
                        <tr key={`${med.medicine}-${line}`}>
                          <td>{line === 0 ? med.medicine : ''}</td>
                          <td>{line === 0 ? med.reason : ''}</td>
                          <td>
                            {line === 0
                              ? [med.minGap ? `${med.minGap} minutes apart` : '', med.max24h ? `most ${med.max24h} in 24 hours` : '']
                                  .filter(Boolean)
                                  .join(', ')
                              : ''}
                          </td>
                          <td className="blank" />
                          <td className="blank" />
                          <td className="blank" />
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </>
            ) : null}

            <footer className="sheet-foot">
              Printed {formatWhen(backup.printedAt)} by {backup.printedBy}. Codes: R refused · N nausea ·
              H held on advice · L out · S asleep · W none available · O other, write on the back.
            </footer>
          </article>
        ))}
      </section>
    );
  }

  if (what === 'summary' && summary) {
    return (
      <section className="printout">
        <div className="head no-print">
          <h1>Medication summary</h1>
          <div className="row-actions">
            <button type="button" onClick={() => window.print()}>Print</button>
            <button type="button" className="quiet" onClick={() => setWhat('menu')}>Back</button>
          </div>
        </div>

        <article className="sheet">
          <header className="sheet-head">
            <div>
              <h2>{summary.resident.name}</h2>
              <p>
                {summary.resident.room ? `Room ${summary.resident.room} · ` : ''}
                born {formatDate(summary.resident.dateOfBirth)}
              </p>
              <p>{summary.home.name}{summary.home.phone ? ` · ${summary.home.phone}` : ''}</p>
            </div>
            <div className="sheet-note">
              <strong>Everything they are taking</strong>
              <span>Printed {formatWhen(summary.printedAt)} by {summary.printedBy}</span>
            </div>
          </header>

          <p className={summary.resident.allergies.length ? 'allergy' : 'muted small'}>
            {summary.resident.allergies.length
              ? `ALLERGIES: ${summary.resident.allergies.join(', ')}`
              : 'No allergies recorded'}
          </p>

          <table className="register">
            <thead><tr><th>Medicine</th><th>Directions</th><th>When</th></tr></thead>
            <tbody>
              {summary.medicines.map((med) => (
                <tr key={med.medicine}>
                  <td>{med.medicine}{med.controlledDrug ? <small>Controlled drug</small> : null}</td>
                  <td>{med.directions}</td>
                  <td>{med.schedule}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {summary.whenRequired.length ? (
            <>
              <h3>When required</h3>
              <table className="register">
                <thead><tr><th>Medicine</th><th>Directions</th></tr></thead>
                <tbody>
                  {summary.whenRequired.map((med) => (
                    <tr key={med.medicine}><td>{med.medicine}</td><td>{med.directions}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {summary.inr ? (
            <p>
              <strong>Warfarin:</strong> INR {summary.inr.value} on {formatDate(summary.inr.testedOn)}, giving{' '}
              {summary.inr.doseText}
              {summary.inr.nextTestOn ? `. Next test ${formatDate(summary.inr.nextTestOn)}.` : '.'}
            </p>
          ) : null}

          {summary.fluids?.thickenerLevel ? (
            <p><strong>Drinks:</strong> thickened to level {summary.fluids.thickenerLevel}. {summary.fluids.thickenerNote}</p>
          ) : null}

          {summary.recentlyStopped.length ? (
            <>
              <h3>Stopped in the last two months</h3>
              <ul>
                {summary.recentlyStopped.map((med) => (
                  <li key={med.medicine}>
                    {med.medicine} — stopped {formatDate(med.stoppedOn)}
                    {med.reason ? `, ${med.reason}` : ''}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </article>
      </section>
    );
  }

  return (
    <section>
      <h1>Printouts</h1>
      {error ? <p className="error" role="alert">{error}</p> : null}

      <ul className="list">
        <li>
          <div className="row static column">
            <span className="row-main">Backup chart for the week ahead</span>
            <p className="muted small">
              Blank boxes to sign, for when the system, a tablet or the Wi-Fi is down. Print it now and keep it
              in the folder: it is no use printing it when the power is off.
            </p>
            <div className="row-actions">
              <button type="button" onClick={() => void loadBackup()}>Build it</button>
            </div>
          </div>
        </li>
        <li>
          <div className="row static column">
            <span className="row-main">Medication summary for one resident</span>
            <p className="muted small">
              One page of everything they take, for the ambulance crew, the hospital or the GP.
            </p>
            <div className="row-actions">
              {residents.map((resident) => (
                <button type="button" className="quiet" key={resident.id} onClick={() => void loadSummary(resident.id)}>
                  {resident.firstName} {resident.lastName}
                </button>
              ))}
            </div>
          </div>
        </li>
      </ul>
    </section>
  );
}

/*
 * "ONE tablet at 08:00 from 2026-09-21 to 2026-09-25" becomes "from 21 Sep to 25 Sep". Without it
 * a reducing course prints as three rows at the same time, and somebody gives all three.
 */
function qualifier(when: string): string {
  return when
    .replace(/^.*?at \d{2}:\d{2}\s*/, '')
    .replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, year: string, month: string, day: string) =>
      new Date(`${year}-${month}-${day}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    )
    .trim();
}

function formatDate(date: string): string {
  if (!date) return '';
  return new Date(`${date.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)}/${when.getFullYear()} at ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

function shortDay(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
}

function shortDate(date: string): string {
  const [, m, d] = date.split('-');
  return `${d}/${m}`;
}
